import React from 'react';

import { graphql } from 'gatsby';

import { Button, Grid2 as Grid, useTheme } from '@mui/material';

import { ExternalLinkIcon, ResponsiveGrid } from '@mewa/ui';

import {
  markdownToHTML,
  parseHTMLForHeadings,
} from '../../../../libs/markdown-to-html';
import {
  ContentSectionItem,
  PossibleContentSections,
} from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';
import { StrapiImage, StrapiImageData } from './image';
import { MarkdownContentWithImage } from './markdown-content-with-image-description';

export const query = graphql`
  fragment FlexibleContentFragment on STRAPI__COMPONENT_SHARED_FLEXIBLE_CONTENT {
    richtext {
      data {
        childMarkdownRemark {
          html
          headings {
            value
            depth
          }
        }
      }
    }
    image {
      ...StrapiImageFileFragment
    }
    alignImage
    strapi_component
    showImageDropShadow
    cta {
      text
      url
    }
  }
`;

export type FlexibleContentData = {
  richtext: {
    data: {
      childMarkdownRemark: {
        html: string;
        headings: {
          value: string;
          depth: number;
        }[];
      };
    };
  };
  image: StrapiImageData | null;
  alignImage: 'left' | 'right';
  strapi_component: typeof componentName;
  showImageDropShadow: boolean;
  cta?: {
    text: string;
    url: string;
  } | null;
};

export type FlexibleContentPreviewData = FlexibleContentData & {
  richtext: string;
};

export const componentName = 'shared.flexible-content';

const isComponent = (
  section: PossibleContentSections,
): section is ContentSectionItem<FlexibleContentData> =>
  section.strapi_component === componentName;

export const FlexibleContent = (
  data: ContentSectionItem<FlexibleContentData>,
) => {
  const theme = useTheme();

  const renderButton = () => {
    if (!data.cta) {
      return;
    }
    const linkIsExternal = data.cta.url?.startsWith('http');

    return (
      <Button
        variant="contained"
        color="primary"
        href={data.cta.url}
        target={linkIsExternal ? '_blank' : undefined}
        rel={linkIsExternal ? 'noreferrer' : undefined}
        startIcon={linkIsExternal ? <ExternalLinkIcon /> : undefined}
        style={{ marginTop: 'var(--spacer-l)' }}
      >
        {data.cta.text}
      </Button>
    );
  };

  if (data.image) {
    return (
      <Grid
        container
        columns={2}
        columnSpacing="var(--gutter)"
        rowSpacing={0}
        style={{
          flexDirection:
            data.alignImage === 'right' ? 'row-reverse' : undefined,
        }}
        id={data.id}
      >
        <Grid size={{ xs: 2, sm: 1 }}>
          <StrapiImage
            image={data.image}
            style={{
              width: '100%',
              marginBottom: { xs: '10px', sm: 0 },
              objectFit: 'cover',
              aspectRatio: 335 / 188,

              boxShadow: data.showImageDropShadow
                ? '0px 0px 24px 4px rgba(0, 0, 0, 0.17)'
                : 'none',

              [theme.breakpoints.up('sm')]: { aspectRatio: 494 / 272 },
              [theme.breakpoints.up('md')]: { aspectRatio: 738 / 410 },
              [theme.breakpoints.up('lg')]: { aspectRatio: 942 / 555 },
            }}
          />
        </Grid>
        <Grid size={{ xs: 2, sm: 1 }}>
          <MarkdownContentWithImage
            html={data.richtext.data.childMarkdownRemark.html}
          />
          {renderButton()}
        </Grid>
      </Grid>
    );
  } else {
    return (
      <>
        <MarkdownContentWithImage
          id={data.id}
          html={data.richtext.data.childMarkdownRemark.html}
        />
        {renderButton()}
      </>
    );
  }
};

const addTableOfContentsHeadline = (
  data: FlexibleContentData,
): ContentSectionItem<FlexibleContentData> => {
  const heading =
    parseHTMLForHeadings(data.richtext.data.childMarkdownRemark.html).find(
      (heading) => heading.depth === 2,
    )?.value ?? '';

  return {
    ...data,
    heading,
  };
};

export const GridWrapperFlexibleContent = (
  data: ContentSectionItem<FlexibleContentData>,
) => {
  return (
    <ResponsiveGrid>
      <Grid size={12}>
        <FlexibleContent {...data} />
      </Grid>
    </ResponsiveGrid>
  );
};

export const config = {
  componentName,
  isComponent,
  Component: FlexibleContent,
  addTableOfContentsHeadline,
};

export const campaignConfig = {
  componentName,
  isComponent,
  Component: GridWrapperFlexibleContent,
  addTableOfContentsHeadline,
};

export const previewConfig: ContentLivePreviewConfig = {
  componentName,
  populationParams: {
    richtext: '*',
    image: '*',
    cta: '*',
  },
  mapProperties: (data) =>
    data.strapi_component === componentName
      ? {
          ...data,
          richtext: markdownToHTML(data.richtext),
        }
      : null,
};
